import { useCallback, useMemo } from 'react'

import { type OnlyFansMessage, VaultMediaType } from '@api'

import VaultAudioIcon from '@/assets/icons/icon-vault-audio.svg'
import VaultGifIcon from '@/assets/icons/icon-vault-gif.svg'
import VaultImageIcon from '@/assets/icons/icon-vault-image.svg'
import VaultVideoIcon from '@/assets/icons/icon-vault-video.svg'
import { classNames } from '@/utils/helpers'

interface Props {
  message: OnlyFansMessage
}

export const ChatMediaCounter = ({ message }: Props) => {
  const counts = useMemo(() => {
    return (message.media ?? []).reduce(
      (acc, media) => {
        if (media.type === VaultMediaType.PHOTO) {
          acc.photo++
        }
        if (media.type === VaultMediaType.VIDEO) {
          acc.video++
        }
        if (media.type === VaultMediaType.AUDIO) {
          acc.audio++
        }
        if (media.type === VaultMediaType.GIF) {
          acc.gif++
        }

        return acc
      },
      {
        [VaultMediaType.PHOTO]: 0,
        [VaultMediaType.VIDEO]: 0,
        [VaultMediaType.AUDIO]: 0,
        [VaultMediaType.GIF]: message.giphyId ? 1 : 0,
      },
    )
  }, [message])

  const renderMediaCount = useCallback(
    (type: VaultMediaType, count: number) => {
      if (count === 0) {
        return null
      }
      let Icon = null

      switch (type) {
        case VaultMediaType.PHOTO:
          Icon = VaultImageIcon
          break
        case VaultMediaType.VIDEO:
          Icon = VaultVideoIcon
          break
        case VaultMediaType.AUDIO:
          Icon = VaultAudioIcon
          break
        case VaultMediaType.GIF:
          Icon = VaultGifIcon
          break
        default:
          break
      }

      return (
        <li className="flex items-center gap-1">
          {Icon && (
            <Icon
              className={classNames(
                'h-4 w-4 stroke-black transition dark:stroke-white',
              )}
            />
          )}
          <span className="text-xs font-medium text-black dark:text-white">
            {count}
          </span>
        </li>
      )
    },
    [],
  )

  return (
    <ul className="flex gap-2">
      {Object.entries(counts).map(([type, count]) =>
        renderMediaCount(type as VaultMediaType, count),
      )}
    </ul>
  )
}
